import { DatePipe } from '@angular/common';
import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from "@angular/material/dialog";
import { AbstractComponent, ConfirmationComponent, Flow, FlowService, Pager, UtilHelper } from "lib-trend-core";
import { Subject, debounceTime, distinctUntilChanged, switchMap } from "rxjs";
import { environment } from "../../../environments/environment";
import { FlowFormComponent } from "./flow-form/flow-form.component";

@Component({
    selector: 'flow-component',
    templateUrl: 'flow.component.html',
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FlowComponent extends AbstractComponent implements OnInit {
  pager: Pager<Flow> = new Pager<Flow>({ perPage: 50 });
  fomattedDate: string

  displayedColumns: string[] = ['name', 'description', 'actions']

  // Debounced search over the company-scoped /pager (house pattern — see contact-list).
  private searchSubject = new Subject<string>()

  // Status filter (house pattern — mat-menu like Tarefas > Prioridade). '' is not used; null = Todos.
  statusLabel = 'Status'
  selectedStatus: string | null = null

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private flowService: FlowService,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(() => this.flowService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)),
    ).subscribe({
      next: (pager: Pager<Flow>) => this.pager = pager,
      error: (err) => this.alertService.error(err.error.message),
    });
    this.getFlows();
  }

  search(term: string) {
    this.pager.page = 1;
    this.searchString = term;
    this.searchSubject.next(term);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getFlows();
  }

  changeStatus(value: string | null): void {
    this.selectedStatus = value;
    this.statusLabel = value === 'true' ? 'Publicado' : value === 'false' ? 'Rascunho' : 'Status';
    this.pager.page = 1;
    this.getFlows();
  }

  openDialog(): void {
    this.dialog.open(FlowFormComponent, { width: '480px' })
  }

  // Row click → open the flow editor (same target as the "Editar" action). House pattern (see
  // task.goToTaskDetail).
  edit(flowId: string): void {
    this.router.navigate(['/flow/edit/', flowId])
  }

  // RF-53: hover label listing the channels this flow currently drives.
  activeChannelsLabel(item: Flow): string {
    const names = (item.activeChannels ?? []).map((channel) => channel.name).filter(Boolean)
    return names.length ? `Ativo em: ${names.join(', ')}` : 'Ativo'
  }

  delete(flowId: string) {
    // RF-137: unconditional confirmation before delete (no reverse-reference lookup).
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: 'Esse fluxo pode estar sendo referenciado em outro fluxo. Deseja mesmo excluir?',
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        this.flowService.delete(flowId).subscribe({
          next: (value) => {
            this.getFlows()
            this.alertService.success()
          },
          error: (err) => this.alertService.error(err.error.message),
        })
      }
    })
  }

  unpublish(flowId: string) {
    // RF-137: unconditional confirmation before unpublish (no reverse-reference lookup).
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: 'Esse fluxo pode estar sendo referenciado em outro fluxo. Deseja mesmo despublicar?',
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        this.flowService.unpublish(flowId).subscribe({
          next: () => {
            this.getFlows()
            this.alertService.success()
          },
          error: (err) => this.alertService.error(err.error.message),
        })
      }
    })
  }

  showOptions(id: string) {
    this.goToWebService(id)
  }

  goToWebService(id: string) {
    const idUser = super.getIDCurrentUser();
    const idCompany = super.getIDCurrentCompany();
    const base64 = UtilHelper.generateTokenTowardsApps(idUser, idCompany);
    window.open(`${environment.trendFlow}/${id}?token=${base64}`, '_blank');
  }

  getFlows() {
    // Build the status filter into searchParams (backend /pager reads isPublished=true/false;
    // absent = Todos). The debounced search reuses this same searchParams.
    this.searchParams = {};
    if (this.selectedStatus === 'true' || this.selectedStatus === 'false') {
      this.searchParams['isPublished'] = this.selectedStatus;
    }
    this.flowService.getAll(
      this.pager.page,
      this.pager.perPage,
      this.searchString,
      this.searchParams).subscribe({
        next: (pager: Pager<Flow>) => {
          this.pager = pager
        },
        error: (err) => this.alertService.error(err.error.message),
      })
  }
}


